import { Grid, InputAdornment, Typography } from "@mui/material";
import { useCampaigns } from "api/campaigns";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { get } from "lodash";
import { useState } from "react";
import {
  CampaignCompletionTimeframe,
  CampaignCompletionType,
  Timeframes,
  campaignCompletionTimeframes,
} from "types/segments";
import { formatNumberValue } from "utilities/numbers";
import { pluralizeUnit } from "utilities/strings";
import { BoxElement } from "./BoxElement";
import { useStyles } from "./SegmentConditions.styles";

export const initialValuesCampaignCompletion: CampaignCompletionType = {
  campaignId: "",
  timeframe: {
    type: Timeframes.LAST_X_DAYS,
    number: 0,
  },
  completionNumber: 0,
  type: "campaign_completion",
};

export function CampaignCompletion({
  values,
  status,
  formField,
  readOnly,
}: {
  status?: any;
  values: any;
  formField: string;
  readOnly?: boolean;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const value = get(values, formField);

  const [campaignSearch, setCampaignSearch] = useState("");
  const { useInfiniteCampaignsList, useCampaignData } = useCampaigns();

  const {
    data: infiniteCampaignsList,
    size: campaignsSize,
    setSize: setCampaignsSize,
    isValidating,
  } = useInfiniteCampaignsList({
    filters: campaignSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: campaignSearch,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const {
    infiniteData: campaignsList,
    anyElementsToFetch: anyCampaignsToFetch,
  } = useManageInfiniteScroll(infiniteCampaignsList || []);

  const { data: selectedCampaign } = useCampaignData(value?.campaignId);

  const campaignsItems = campaignsList
    ? campaignsList.map(({ name, campaignId }) => ({
        id: campaignId,
        label: name,
      }))
    : [];

  const timeframesItems = campaignCompletionTimeframes.map((timeframe) => ({
    label: message({ id: timeframe.label }),
    id: timeframe.value,
  }));

  const getCurrentTimeframe = (timeframeType: CampaignCompletionTimeframe) => {
    switch (timeframeType) {
      case Timeframes.LAST_X_DAYS:
        return "days";
      case Timeframes.LAST_X_WEEKS:
        return "weeks";
      case Timeframes.LAST_X_MONTHS:
        return "months";
      default:
        return "";
    }
  };

  const getSingularTimeframe = (timeframe: string) => {
    switch (timeframe) {
      case "days":
        return "day";
      case "weeks":
        return "week";
      case "months":
        return "month";
      default:
        return "";
    }
  };

  const dataTestId = "segment-form-conditions-campaign-completion";
  const currentTimeframe = getCurrentTimeframe(value?.timeframe.type);
  const singularCurrentTimeframe = getSingularTimeframe(currentTimeframe);
  const isValueEmpty = (value: number | string) => value === 0 || value === "";
  const campaignIdError = get(status, `${formField}.campaignId`);
  const timeframeNumberError = get(status, `${formField}.timeframe.number`);
  const completionNumberError = get(status, `${formField}.completionNumber`);

  return (
    <>
      {value.isConditionSaved || readOnly ? (
        <>
          <BoxElement
            content={
              campaignsItems.find((item) => item.id === value.campaignId)
                ?.label || value.campaignId
            }
            error={campaignIdError}
          />
          {message({ id: "overTheLast" }).toLocaleLowerCase()}
          <BoxElement
            content={formatNumberValue(value?.timeframe?.number, "")}
            error={timeframeNumberError}
          />
          {message({ id: currentTimeframe }).toLocaleLowerCase()}
          <BoxElement
            content={formatNumberValue(value?.completionNumber, "")}
            error={completionNumberError}
          />
          {message({ id: "times" }).toLocaleLowerCase()}
        </>
      ) : (
        <>
          <SelectInput
            className={classes.campaignCompletionInput}
            displaySearch
            data-test-id={`${dataTestId}-campaign-name`}
            name={`${formField}.campaignId`}
            label={
              <RequiredFieldLabel text={message({ id: "campaignName" })} />
            }
            items={campaignsItems}
            asyncSearch={(searchedQuery: string) =>
              setCampaignSearch(searchedQuery)
            }
            initialValue={
              selectedCampaign && {
                id: selectedCampaign?.campaignId,
                label: selectedCampaign?.name,
              }
            }
            placeholder={message({ id: "searchForCampaign" })}
            loadMoreElements={() =>
              !isValidating && setCampaignsSize(campaignsSize + 1)
            }
            anyElementsToFetch={anyCampaignsToFetch}
          />
          <Grid container spacing={10}>
            <Grid item xs={6}>
              <SelectInput
                className={classes.campaignCompletionInput}
                data-test-id={`${dataTestId}-timeframe`}
                name={`${formField}.timeframe.type`}
                label={
                  <RequiredFieldLabel
                    text={message({ id: "campaignCompletionTimeframe" })}
                  />
                }
                items={timeframesItems}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberFormatInput
                allowNegative={false}
                className={classes.campaignCompletionInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {message({
                        id: "times",
                      }).toLowerCase()}
                    </InputAdornment>
                  ),
                }}
                label={
                  <RequiredFieldLabel
                    text={message({ id: "campaignCompletionNumber" })}
                  />
                }
                name={`${formField}.completionNumber`}
                data-test-id={`${dataTestId}-number`}
              />
            </Grid>
          </Grid>
          <div className={classes.campaignCompletionWrapper}>
            <Typography mr={12}>
              {message({ id: "over" }).toLowerCase()}
            </Typography>
            <NumberFormatInput
              allowNegative={false}
              className={classes.campaignCompletionInput}
              data-test-id={`${dataTestId}-timeframe-number`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {message({
                      id: currentTimeframe,
                    }).toLowerCase()}
                  </InputAdornment>
                ),
              }}
              label={
                <RequiredFieldLabel
                  text={message(
                    {
                      id: "campaignCompletionTimeframeNumberInfo",
                    },
                    {
                      timeframe: currentTimeframe,
                    },
                  )}
                />
              }
              name={`${formField}.timeframe.number`}
            />
          </div>
          <Typography className={classes.campaignCompletionInfo}>
            {message(
              {
                id: isValueEmpty(value?.timeframe.number)
                  ? "campaignCompletionInCurrentPeriodInfo"
                  : "campaignCompletionInfo",
              },
              {
                timeframe: isValueEmpty(value?.timeframe.number)
                  ? singularCurrentTimeframe
                  : pluralizeUnit(
                      value?.timeframe.number,
                      singularCurrentTimeframe,
                      currentTimeframe,
                    ),
                completionNumber: isValueEmpty(value?.completionNumber)
                  ? `0 ${message({ id: "times" }).toLowerCase()}`
                  : pluralizeUnit(value?.completionNumber, "time", "times"),
              },
            )}
          </Typography>
        </>
      )}
    </>
  );
}
